import { useEffect, useState, useRef } from 'react'
import { graphql, useRelayEnvironment, fetchQuery } from 'react-relay'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { captureException } from '@sentry/react'

import { useAuthContext } from './useAuthContext'

import { useAuthRedirectQuery } from './__generated__/useAuthRedirectQuery.graphql'

export const useAuthRedirect = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const relayEnvironment = useRelayEnvironment()

  const authContext = useAuthContext()

  // const [authRedirecting, setAuthRedirecting] = useState(false)

  // Don't `resolve` for the moment, as the page will navigate
  // Which will unmount the whole form

  // const authRedirectingResolve = useRef<(value: void) => void>(
  //   (value) => undefined
  // )
  const authRedirectingReject = useRef<(reason?: any) => void>(
    (reason) => undefined
  )

  const authRedirectingPromise = useRef(
    new Promise<void>((resolve, reject) => {
      // authRedirectingResolve.current = resolve
      authRedirectingReject.current = reject
    })
  )

  // const authRedirectingRef = useRef(() => {})

  useEffect(() => {
    if (!authContext.accessToken) {
      return
    }

    // If there's a code in the URL, the user might be verifying an email
    // while already signed in. Don't redirect — let the form process the code first.
    if (searchParams.has('code')) {
      return
    }

    // setAuthRedirecting(true)
    ;(async () => {
      try {
        let data
        let numberOfAttempts = 3

        do {
          if (numberOfAttempts === 0) {
            console.log('Max number of attempts trying to log in')
            authRedirectingReject.current(false)
            // setAuthRedirecting(false)
            return
          }

          try {
            data = await fetchQuery<useAuthRedirectQuery>(
              relayEnvironment,
              graphql`
                query useAuthRedirectQuery {
                  viewer {
                    id
                  }
                }
              `,
              {}
            ).toPromise()
          } finally {
            numberOfAttempts = numberOfAttempts - 1
          }
        } while (
          !data?.viewer &&
          (await new Promise<true>((resolve) =>
            setTimeout(() => resolve(true), 100)
          ))
        )

        const authRedirectParam = searchParams.get('authRedirectPath')

        if (authRedirectParam) {
          navigate(decodeURIComponent(authRedirectParam))
          return
        }

        if (location.state?.authRedirectPath) {
          navigate(location.state.authRedirectPath)
          return
        }

        if (location.state?.backgroundLocation) {
          const backgroundLocation = location.state
            .backgroundLocation as Location

          navigate(`${backgroundLocation.pathname}${backgroundLocation.search}`)
          return
        }

        navigate('/')
      } catch (err) {
        console.error('COULD NOT AUTH REDIRECT')
        console.error(err)

        captureException(err)

        // setAuthRedirecting(false)
        authRedirectingReject.current(false)
      } finally {
        authRedirectingPromise.current = new Promise<void>(
          (resolve, reject) => {
            // authRedirectingResolve.current = resolve
            authRedirectingReject.current = reject
          }
        )
      }
    })()
  }, [
    authContext,
    // setAuthRedirecting,
    relayEnvironment,
    location.state?.authRedirectParam,
    location.state?.backgroundLocation,
    searchParams,
    authRedirectingPromise,
    authRedirectingReject,
  ])

  return authRedirectingPromise.current
}
